/**
 * ClipCard — pure card for one inbound clip (no platform APIs/store; `ClipboardScreen` wires those).
 * Content is MASKED when `sensitive`/`forceMask` (public mode) until reveal-on-tap; the copy button is the ONLY OS-clipboard write, gated on explicit click.
 * SECURITY: clip `content` is decrypted plaintext — never logged.
 */

import { Check, Copy01, Eye, EyeOff, Trash01 } from "@untitledui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatRelativeTime, type RelativeTimeLabels } from "../../utils/format";
import { IconButton } from "../IconButton";
import styles from "./ClipCard.module.css";

export interface ClipCardLabels {
  /**
   * Text for the relative timestamp on this card. Required: it was English-only
   * inside `formatRelativeTime` until desktop slice 5 (R1-f), which is how
   * "Zuletzt verwendet just now" reached German users.
   */
  relativeTime: RelativeTimeLabels;
  /** "from {label}" provenance prefix — passed the origin label. Used for received clips. */
  from: (label: string) => string;
  /**
   * "to {label}" provenance prefix — passed the destination label. Used for
   * sent clips. Optional so hosts that haven't started tagging
   * `direction: "sent"` yet (PR1) keep compiling without a label update;
   * when omitted, the caption falls back to the already-localized `from`
   * rather than an unlocalized literal. Every direction-aware caller should
   * supply a real "to" translation.
   */
  to?: (label: string) => string;
  /** aria-label for the copy button. */
  copy: string;
  /** aria-label for the copy button once copied (brief confirmation). */
  copied: string;
  /** aria-label for the reveal toggle when content is masked. */
  reveal: string;
  /** aria-label for the reveal toggle when content is shown. */
  hide: string;
  /** Visible chip text marking a sensitive clip. */
  sensitive: string;
  /** Cloud-provenance chip text ("from your API token"). */
  cloud: string;
  /**
   * aria-label for the delete control. Optional so hosts that haven't wired
   * `onDelete` yet (PR1) keep compiling without a label update. The control
   * renders only when BOTH `onDelete` AND this label are supplied — a host
   * that wires the callback without a translation gets a visibly missing
   * button instead of an unlocalized English string (this package isn't
   * covered by `l10n:check`).
   */
  delete?: string;
  /**
   * sr-only text announced while content is masked; also surfaces as the
   * disabled copy control's `title` (round 3, D6) so a sighted user sees
   * *why* the button is dead, not just that it is. Optional, and gated on
   * presence with no fallback (D7): a host that hasn't wired this key yet
   * gets no hint at all rather than an unlocalized English string leaking
   * to every locale (this package isn't covered by `l10n:check`).
   */
  maskedHint?: string;
}

export interface ClipCardProps {
  /** Stable clip id — passed back verbatim to `onDelete`. */
  id: string;
  content: string;
  /** Origin device/surface display name (sender for received, destination for sent). */
  originLabel: string;
  /** Capture timestamp (ms) — rendered as relative time. */
  timestamp: number;
  /** Whether this clip was sent from this device or received from a peer — picks the "to"/"from" caption. */
  direction: "sent" | "received";
  /** Sender-asserted sensitive hint — masks the preview until revealed. */
  sensitive?: boolean;
  /** Privacy ("public") mode — masks the preview regardless of the sender hint. */
  forceMask?: boolean;
  /** Trust tier; "cloud" surfaces a wary "from your API token" chip. */
  provenance: "device" | "cloud";
  /** Copy content to the OS clipboard (host-supplied `writeText`); invoked only on click, resolves to flash "copied". */
  onCopy: (content: string) => Promise<void>;
  /** Delete this clip from history. Renders the delete control only when supplied. */
  onDelete?: (id: string) => void;
  /**
   * Bump this to force the card to re-mask (round 3, D5 — the PC analogue
   * of the clipboard-manager spec's VM-held reveal reset on `ON_STOP`).
   * Only the value's IDENTITY change matters, not the number itself — same
   * "just needs a fresh value" contract as `ClipboardScreen`'s own
   * `dayTick`. The reset applies unconditionally (no `sensitive`/
   * `forceMask` branching): it's a no-op on a non-maskable card since
   * `revealed` has no visible effect there, and applying it unconditionally
   * is what keeps ONE listener in `ClipboardScreen` (document-hidden)
   * sufficient instead of needing per-card mode logic.
   */
  resetSignal?: number;
  labels: ClipCardLabels;
}

export function ClipCard({
  id,
  content,
  originLabel,
  timestamp,
  direction,
  sensitive,
  forceMask,
  provenance,
  onCopy,
  onDelete,
  resetSignal,
  labels,
}: ClipCardProps) {
  // A clip is masked when the sender flags it sensitive OR public mode
  // is on; either case renders the reveal toggle so the user can show
  // the content on demand. Non-masked clips show content directly.
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  // True while onCopy() is awaiting; gates the Copy button so a fast
  // double-click can't fire two concurrent writes.
  const [copying, setCopying] = useState(false);
  // Holds the "copied" flash timer so we can cancel it if the card
  // unmounts within 1.5s of a copy (e.g. clearClips() on disconnect),
  // avoiding a setState on an unmounted component.
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Tracks mount state so the async copy `.then()` can't setState after
  // the card unmounts (e.g. clearClips() on disconnect races a copy).
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(copiedTimer.current);
    };
  }, []);

  // Re-mask an already-revealed clip when public mode turns on, so toggling
  // privacy back on doesn't leave revealed plaintext visible.
  useEffect(() => {
    if (forceMask) setRevealed(false);
  }, [forceMask]);

  // Round 3 (D5): re-mask whenever the host bumps `resetSignal` (driven by
  // ClipboardScreen's single document-hidden listener) — a one-click reveal
  // that survives a tab switch or the panel going out of sight defeats the
  // whole point of privacy mode. Unconditional (no masked/sensitive/
  // forceMask branching): harmless on a non-maskable card, since `revealed`
  // has no visible effect there. `resetSignal` isn't referenced in the
  // body — only its identity change matters, the same "just needs a fresh
  // value" trigger `ClipboardScreen`'s own `dayTick` effect uses.
  // biome-ignore lint/correctness/useExhaustiveDependencies: resetSignal's identity change (not its value) is what should re-run this effect — see the comment above.
  useEffect(() => {
    setRevealed(false);
  }, [resetSignal]);

  const maskable = sensitive === true || forceMask === true;
  const masked = maskable && !revealed;

  // Caption text (R3/R4): direction is carried by this one caption line —
  // "↑ to <device>" / "↓ from <device>" — never by colour or alignment
  // (locked: alignment fails at 380px, and colour is reserved for the
  // bridge/encryption elsewhere). `toLabel` is only truthy when BOTH the
  // clip is actually sent AND the host has wired `labels.to` — the same
  // gate decides which word ("to"/"from") AND which glyph ("↑"/"↓") get
  // used, so the two can never disagree. Without that gate (e.g. a host
  // mid-rollout that tags `direction: "sent"` before wiring `labels.to`,
  // per the field's own PR1 note), the caption falls all the way back to
  // "↓ from <device>" — glyph and word agreeing on "received-shaped"
  // wording — rather than pairing the sent glyph with "from" text.
  const toLabel = direction === "sent" ? labels.to : undefined;
  const captionLabel = toLabel ? toLabel(originLabel) : labels.from(originLabel);
  const directionGlyph = toLabel ? "↑" : "↓";

  // What actually renders in the `<pre>` — the bullet placeholder while
  // masked, the real text otherwise. Checked (not `content` directly) so
  // the multiline modifier below reflects what's on screen: the masked
  // placeholder never has a newline, so a masked sensitive clip always
  // gets the tighter single-line-shaped clamp regardless of what the
  // real content looks like.
  const displayText = masked ? "••••••••••••" : content;
  const contentClasses = [
    styles.content,
    displayText.includes("\n") ? styles.contentMultiline : null,
  ]
    .filter(Boolean)
    .join(" ");

  const handleCopy = useCallback(() => {
    // Fire-and-forget with a transient "copied" flash. The write itself
    // happens inside this click handler so it carries user activation.
    setCopying(true);
    void onCopy(content).then(
      () => {
        if (!mountedRef.current) return;
        setCopying(false);
        setCopied(true);
        clearTimeout(copiedTimer.current);
        copiedTimer.current = setTimeout(() => setCopied(false), 1500);
      },
      () => {
        // Swallow — the host logs the failure (never logs content). A
        // failed copy just doesn't flash the confirmation.
        if (mountedRef.current) setCopying(false);
      }
    );
  }, [content, onCopy]);

  return (
    <div className={styles.card}>
      {/* Header row (2026-08-05 card-language redesign, C1; round 3 D1/D2
          reworks the trailing side): mirrors Notification's
          headerRow/headerMeta — direction glyph + peer label + relative
          time on the left, reveal/copy/delete trailing on the right. The
          standalone `.actions` row from C1 is gone (D1): those three
          controls now live here, and the sensitive/cloud chips move into
          `headerMeta` below (rare, and — fix round — they get their own
          explicit `{" "}` leading space each, same as every other segment
          in this line, so `headerMeta`'s normal inline text flow actually
          HAS a break opportunity to wrap on; the original D1 cut corners
          here and shipped chips directly adjacent to the timestamp with no
          text between them at all, which JSX's whitespace-collapsing rules
          made into a genuine zero-width join, not just a visually tight
          one — see `.chip` in ClipCard.module.css for the other half).
          The glyph and the "·" separator are `aria-hidden`: direction is
          already carried by the word ("to"/"from"), so the glyph is purely
          decorative (see the docblock on `toLabel` above — a screen reader
          would otherwise announce "up arrow" / "middle dot" for no
          semantic gain, same rationale as Notification's app-name/time
          separator and FooterHint's key separators). The surrounding
          `{" "}` are real (non-hidden) text nodes so "to Pixel 8" and the
          relative time still read as two separated words, not run together. */}
      <div className={styles.headerRow}>
        <div className={styles.headerMeta}>
          <span className={styles.glyph} aria-hidden="true">
            {directionGlyph}
          </span>{" "}
          <span className={styles.peerLabel}>{captionLabel}</span>{" "}
          <span className={styles.separator} aria-hidden="true">
            ·
          </span>{" "}
          <span className={styles.timestamp}>
            {formatRelativeTime(timestamp, labels.relativeTime)}
          </span>
          {/* Fix round: the leading `{" "}` is a REQUIRED real text node, not
              decoration — JSX strips a whitespace-only line between elements,
              so without it the timestamp and the chip render with no space
              between them at all, and a header row with no whitespace
              anywhere has no line-break opportunity: it overflows a narrow
              side panel instead of wrapping (see ClipCard.module.css's
              `.chip` comment for the other half of this fix). */}
          {sensitive && (
            <>
              {" "}
              <span className={styles.chip} data-tone="sensitive">
                {labels.sensitive}
              </span>
            </>
          )}
          {provenance === "cloud" && (
            <>
              {" "}
              <span className={styles.chip} data-tone="cloud">
                {labels.cloud}
              </span>
            </>
          )}
        </div>
        <div className={styles.headerActions}>
          <div className={styles.primaryActions}>
            {maskable && (
              <IconButton
                icon={revealed ? EyeOff : Eye}
                label={revealed ? labels.hide : labels.reveal}
                size="s"
                onClick={() => setRevealed((v) => !v)}
              />
            )}
            <IconButton
              icon={copied ? Check : Copy01}
              label={copied ? labels.copied : labels.copy}
              size="s"
              onClick={handleCopy}
              // Require reveal before copy so the user sees what they're copying (sensitive is a mask hint, not a security boundary); also gate while a write is in-flight (double-click guard).
              disabled={masked || copying}
              // D6: while masked, explain the disabled state in the native
              // hover tooltip rather than leaving it sr-only — a sighted
              // user otherwise sees a dead button with no visible cause.
              // Gated on the label's presence (D7): no hint supplied means
              // no override, so IconButton's own `title ?? label` fallback
              // shows the plain "Copy"/"Copied" text instead of a
              // hardcoded English string.
              title={masked ? labels.maskedHint : undefined}
            />
          </div>
          {/* Gated on BOTH the callback and its label — a host that wires
              `onDelete` without a translated `labels.delete` gets a visibly
              missing button (caught in review/QA) rather than a silent
              English string leaking to every locale (this package isn't
              scanned by l10n:check). Kept OUT of `.primaryActions` (D2): a
              wider gap and a demoted tertiary tone (see the CSS) separate
              it from copy/reveal, the same position Notification uses for
              a merely-cosmetic dismiss — here a misclick permanently
              destroys the only copy of persisted plaintext. */}
          {onDelete && labels.delete && (
            <IconButton
              icon={Trash01}
              label={labels.delete}
              size="s"
              onClick={() => onDelete(id)}
              className={styles.deleteButton}
            />
          )}
        </div>
      </div>

      <pre
        className={contentClasses}
        data-masked={masked || undefined}
        aria-hidden={masked || undefined}
        // Hover preview (no OS-clipboard write); capped at 120 chars so the full
        // decrypted payload never sits in a passive DOM attr. Omitted when masked.
        title={masked ? undefined : [...content].slice(0, 120).join("")}
      >
        {displayText}
      </pre>
      {/* sr-only masked hint — gated on the label's presence (D7), no
          fallback: a host that hasn't wired `labels.maskedHint` gets no
          hint at all rather than an unlocalized English string. */}
      {masked && labels.maskedHint && <span className={styles.srOnly}>{labels.maskedHint}</span>}
      {/* Live region: announces the transient "Copied" confirmation to AT. */}
      <span className={styles.srOnly} role="status">
        {copied ? labels.copied : ""}
      </span>
    </div>
  );
}
